import * as React from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import styled from 'styled-components';
import { UseQueryState, useQuery } from 'urql';

import { useDispatch } from 'react-redux';
import * as colors from '../../shared/colors';
import Loader from '../../shared/components/Loader';
import { parseDate } from '../../shared/dateFns';
import t from '../../shared/translations';
import Error from '../components/Error';
import { Dispatch, loadCalendarData } from '../state';
import { Event } from '../state/calendar/reducer';
import AgendaView from './AgendaView';
import MonthView from './MonthView';
import { IData, query } from './query';

const Wrapper = styled.main`
    padding: 0 15px;
    flex-grow: 1;
    max-width: 1030px;
    width: 100%;
    margin-bottom: 2rem;
`;

const ButtonLink = styled(Link)`
    display: inline-block;
    background: none;
    color: ${colors.text};
    border: 1px solid ${colors.text};
    font-size: 1rem;
    padding: 0.5rem 0.5rem;
    border-radius: 0.65rem;
    margin-right: 0.3rem;
    margin-bottom: 0.3rem;
`;

const EMPTY_LIST: Event[] = [];

function useCalendarData(result: UseQueryState<IData>) {
    const dispatch = useDispatch<Dispatch>()
    const events = React.useMemo(() => {
        if (result.fetching) {
            return EMPTY_LIST;
        } else if (result.error) {
            return EMPTY_LIST;
        }
        let id = 1;
        const events: Event[] = [];
        for (const path of result.data.currentPerson.learningPaths) {
            for (const item of path.learningPathItems) {
                if (item.type === 'event') {
                    events.push({
                        id: id++,
                        type: 'event',
                        start: parseDate(item.event.eventStart),
                        end: parseDate(item.event.eventEnd),
                        label: `${t('player.calendar.label-event')} ${item.event.shortTitle}`,
                        subLabel: path.pathname,
                        url: item.url,
                    });
                }
                if (item.type === 'feedback-task') {
                    if (item.feedbackTask.deadline) {
                        events.push({
                            id: id++,
                            type: 'deadline',
                            start: parseDate(item.feedbackTask.deadline),
                            end: null,
                            label: item.feedbackTask.shortTitle,
                            subLabel: path.pathname,
                            url: item.url,
                        });
                    }
                }
                for (const task of item.tasks) {
                    if (task.deadline) {
                        events.push({
                            id: id++,
                            type: 'deadline',
                            start: parseDate(task.deadline),
                            end: null,
                            label: task.shortTitle,
                            subLabel: path.pathname,
                            url: task.url,
                        });
                    }
                    if (task.unlockDate) {
                        events.push({
                            id: id++,
                            type: 'unlock',
                            start: parseDate(task.unlockDate),
                            end: null,
                            label: `${t('player.calendar.label-unlock')} ${task.shortTitle}`,
                            subLabel: path.pathname,
                            url: null,
                        });
                    }
                    for (const variant of task.taskVariants) {
                        if (variant.variantType === 'event') {
                            events.push({
                                id: id++,
                                type: 'event',
                                start: parseDate(variant.eventStart),
                                end: parseDate(variant.eventEnd),
                                label: `${t('player.calendar.label-event')} ${task.shortTitle}`,
                                subLabel: path.pathname,
                                url: task.url,
                            });
                        }
                    }
                }
                if (item.type === 'task-choice') {
                    if (item.taskChoice.deadline) {
                        events.push({
                            id: id++,
                            type: 'deadline',
                            start: parseDate(item.taskChoice.deadline),
                            end: null,
                            label: item.taskChoice.name,
                            subLabel: path.pathname,
                            url: item.url,
                        });
                    }
                    if (item.taskChoice.unlockDate) {
                        events.push({
                            id: id++,
                            type: 'unlock',
                            start: parseDate(item.taskChoice.unlockDate),
                            end: null,
                            label: `${t('player.calendar.label-unlock')} ${item.taskChoice.name}`,
                            subLabel: path.pathname,
                            url: null,
                        });
                    }
                }
            }
        }
        return events;
    }, [result]);
    React.useEffect(() => {
        if (events.length > 0) {
            dispatch(loadCalendarData(events));
        }
    }, [events]);
}

function Calendar(): JSX.Element {
    const [result] = useQuery<IData>({
        query,
    });
    useCalendarData(result);
    if (result.fetching) {
        return <Wrapper style={{ height: 200 }}>
            <Loader />
        </Wrapper>;
    } else if (result.error) {
        return <Wrapper>
            <Error />
        </Wrapper>;
    }
    return <Wrapper>
        <h1>{t('player.calendar.heading')}</h1>
        <ButtonLink to='/calendar/month'>
            {t('player.calendar.button-month-view')}
        </ButtonLink>
        <ButtonLink to='/calendar/agenda'>
            {t('player.calendar.button-agenda-view')}
        </ButtonLink>
        <Routes>
            <Route path='/calendar/month' element={<MonthView />} />
            <Route path='/calendar/agenda' element={<AgendaView />} />
            <Route path='/calendar' element={<Navigate to='/calendar/month' />} />
        </Routes>
    </Wrapper>;
}

export default Calendar;
